import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { Link } from '@tanstack/react-router';
import {
  KanbanSquareIcon,
  LinkIcon,
  Maximize2Icon,
  Minimize2Icon,
  XIcon,
} from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CopyButton } from '@/shared/components/CopyButton';
import { usePreferencesStore } from '@/shared/store/preferences';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
} from '@/components/ui/sheet';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TASK_TAB_LABELS, type TaskTab } from '../searchParams';
import {
  deriveCardExecutionState,
  hasNonTerminalActivity,
} from '../executionStatus';
import {
  artifactsReadOf,
  landingTab,
  offeredTabs,
  relationsCount,
  resultsCount,
} from '../drawerTabs';
import { useTaskArtifacts } from '../hooks/useTaskArtifacts';
import { useTaskResults } from '../hooks/useTaskResults';
import {
  TaskRelationsPanel,
  useDirectTaskRelations,
} from '@/features/task-relations';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { ResultsPlaceholder } from './ResultsPlaceholder';
import { ExecutionIndicator } from './ExecutionIndicator';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { TaskTitleField } from './TaskTitleField';
import { TaskExecutionsPanel } from './TaskExecutionsPanel';
import { TaskResultsPanel } from './TaskResultsPanel';
import { TaskCommentsPanel } from './TaskCommentsPanel';
import { FilePreviewPanel } from '@/features/workspace-files';
import { canRunAgain, hasLiveExecution } from '../executionStatus';
import { TaskActionsMenu } from './TaskActionsMenu';
import { phaseRepeatsStep, type TaskLocation } from '../locateTask';
import type { BoardStepSection } from '../boardModel';
import { useTaskCommands } from '../hooks/useTaskCommands';
import { useStartLinkedTasks } from '../hooks/useStartLinkedTasks';
import { useHideTask } from '../hooks/useHideTask';
import { useTaskCommandChannel, type TaskCommand } from '../taskCommands';
import { derivePrimaryAction } from '../primaryAction';
import { newCommentIds, newCommentsBlock } from '../newComments';
import { useTaskComments } from '../hooks/useTaskComments';
import {
  PENDING_LABEL,
  usePendingTaskAction,
} from '../hooks/usePendingTaskActions';
import { NewCommentsBlock } from './NewCommentsBlock';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

/** One empty list, so a caller without a board does not churn a new one per render. */
const NO_STEPS: readonly BoardStepSection[] = [];
import type { LabelView, TaskView } from '@/lib/api/generated/model';
import { FollowToggle, useMarkTaskReadOnOpen } from '@/features/notifications';

/** What a palette entry that the task has moved past says instead of acting. */
const NO_LONGER_ON_OFFER =
  'This task has moved on since the palette opened; nothing was changed.';

export interface TaskDrawerProps {
  taskId: string;
  workspaceId: string;
  /**
   * This task's *own* workflow key — passed straight through to
   * `TaskDetailsPanel` for its "Created tasks" header link (spec 115 US2).
   * Optional for the same reason it is there: a caller that has not resolved
   * it yet still gets a working drawer, just without that one link.
   */
  workflowKey?: string;
  /**
   * The task itself, and where the board placed it. Passed in rather than read
   * again here: the board already holds both, and a second read could serve a
   * different snapshot mid-poll, leaving the drawer describing a task
   * differently from the card it opened over.
   *
   * Optional so the drawer still opens for a task the board cannot resolve.
   */
  task?: TaskView;
  location?: TaskLocation;
  /**
   * Every step on the board by id, so the details panel can name a transition
   * by the step it runs. Required, and empty when the board could not be
   * built: an absent map and an empty one would mean the same thing here, and
   * offering both invites a caller to think there is a difference.
   */
  stepLabels: Map<string, string>;
  /**
   * Every step of the task's board in board order — what the header's next
   * action can start at or decide between (spec 125). Absent where the caller
   * has no board; the action then offers only Stop and Run again.
   */
  steps?: readonly BoardStepSection[];
  /** The workspace's label vocabulary, for the details panel's picker. */
  labelRegistry: readonly LabelView[];
  /** Narrow the board to a label pressed on this task (D7). */
  onNarrowToLabel?: (labelId: string) => void;
  /**
   * The tab the address names, or nothing. Absent is a value (spec 095
   * FR-004): the drawer then lands on Results when the task has produced
   * something and nothing is running, and on Details otherwise — once it
   * knows, never by opening on one tab and switching.
   */
  tab?: TaskTab;
  /**
   * `replace` marks the drawer writing back the tab it landed on, so the
   * address matches what is shown without adding a history entry.
   */
  onTabChange: (tab: TaskTab, options?: { replace?: boolean }) => void;
  onClose: () => void;
  /**
   * The workspace file shown beside this task, from the address. Absent: no
   * panel.
   */
  previewPath?: string;
  /**
   * Remove `preview` from the address, leaving `task` and `tab` as they are.
   *
   * Required, not optional, for the reason `onTabChange` is: a host that
   * forgot would render a panel that cannot be dismissed.
   */
  onPreviewClose: () => void;
  /**
   * Offer "Open on board", for a drawer shown somewhere other than its board
   * (spec 111). The key of the task's workflow; the link carries the tab being
   * shown, so the board opens on the same view. Absent on the board itself, and
   * for a draft workflow, which has no board to open.
   */
  openOnBoard?: { workflowKey: string };
  /**
   * Where focus goes when the drawer closes. The sheet returns it to whatever
   * opened it, which on a board is always a card; a host whose drawer can open
   * from the address — a shared link, Back, a related task — has no such
   * element and names one here.
   */
  finalFocus?: ComponentProps<typeof SheetContent>['finalFocus'];
  /**
   * A line above the task's own place, naming what the host page knows about
   * where this task came from — the run page puts the parent task and its
   * `linked_workflow` step here. Absent everywhere else.
   */
  context?: ReactNode;
}

/**
 * How many of a thing a tab holds, where the drawer already has the answer.
 *
 * Both counts now arrive on the task itself, so both are free and correct on
 * first paint. Comments used to be excluded here on the grounds that they were
 * their own read, made only while that tab was open, so a count would have been
 * absent the first time and appeared on the second visit. That is no longer
 * true: `comment_count` rides the workspace aggregate the board already polls,
 * which is also what keeps this number and the one on the card from ever
 * disagreeing — they are the same field.
 *
 * A zero is not a count. An empty tab says "no comments yet" in words once it
 * is opened; a `0` beside its name says the same thing less clearly, on every
 * task, forever.
 */
function tabCount(tab: TaskTab, task: TaskView | undefined): number | null {
  const total =
    tab === 'executions'
      ? (task?.executions?.length ?? 0)
      : tab === 'comments'
        ? (task?.comment_count ?? 0)
        : 0;
  return total > 0 ? total : null;
}

/**
 * The task surface, presented over its board so the board stays visible behind
 * it. Which task is open and which tab is selected are carried in the address,
 * not held here — this component renders what it is told.
 *
 * Built on `ui/sheet` rather than on its own fixed-position markup. The focus
 * trap, the Escape handling, the return-focus-on-close, the scroll lock and the
 * backdrop were all hand-rolled here, and every one of them is behaviour the
 * Base UI dialog underneath the sheet already provides — correctly, and in one
 * place that the secrets form and everything after it share. What this file
 * kept is the part that is actually about tasks: the header, the tab strip and
 * the panel.
 *
 * Two consequences of the move, both deliberate:
 *
 * - The backdrop no longer needs a full-screen `<button>` standing in for
 *   click-outside dismissal, so there is no second "Close task" control that
 *   exists only for the pointer and is hidden from the tab order.
 * - The accessible name stays `aria-label`, not a `SheetTitle`. The heading
 *   here is the task's title, and `aria-labelledby` outranks `aria-label` in
 *   the name calculation — wiring the title up would rename this dialog from
 *   "Task detail" to whatever someone typed into a form, which says less about
 *   what opened, not more.
 */
export function TaskDrawer({
  taskId,
  workspaceId,
  workflowKey,
  task,
  location,
  stepLabels,
  steps = NO_STEPS,
  labelRegistry,
  onNarrowToLabel,
  tab,
  onTabChange,
  onClose,
  previewPath,
  onPreviewClose,
  openOnBoard,
  finalFocus,
  context,
}: TaskDrawerProps) {
  /*
    Close when the task this drawer is showing disappears (a descendant deleted
    elsewhere, or deleted from another tab). Only after it has been seen: a
    shared link opens with no task while the board is still loading, and that
    must not close the drawer.
  */
  const taskSeen = useRef(false);
  useEffect(() => {
    if (task) {
      taskSeen.current = true;
    } else if (taskSeen.current) {
      taskSeen.current = false;
      onClose();
    }
  }, [task, onClose]);

  // Every tab this drawer offers can be filled from the task; the ones that
  // could not are no longer offered (see `TASK_TABS`). What is left is the one
  // case that has nothing to render: a task the board could not hand over.
  const wide = usePreferencesStore((state) => state.taskDrawerWide);
  const toggleTaskDrawerWide = usePreferencesStore(
    (state) => state.toggleTaskDrawerWide,
  );

  /*
    The sheet itself, handed to the file preview as its focus fallback: when
    the path the reader clicked has been re-rendered away, focus belongs on the
    surface they are still working in (FR-037). The panel no longer looks this
    element up by `[data-slot="sheet-content"]` — only this host knows a sheet
    is here at all.
  */
  const sheetContentRef = useRef<HTMLDivElement | null>(null);

  /*
    The description being typed, owned here rather than in the panel that shows
    it. Switching to Comments unmounts that panel, and a draft held inside it
    would die on a tab click — the same loss the Save button exists to prevent,
    arriving by a different route. `null` means nobody is editing.
  */
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);

  const state = task ? deriveCardExecutionState(task, location?.step) : null;

  /*
    The header no longer carries the next-action button (spec 125 FR-002): it
    was confusing there (feedback 2026-10-06), and its Shift+Enter went with
    it. The next action is still a click or a key away elsewhere — Shift+Enter
    on a focused card, drag to move, Stop and Run again in the ⋯ menu — and
    the command palette's "This task" entries, which arrive through the
    command channel naming the step to start at or the way on to take, run
    here without a control to press.
  */
  // Opening a task is reading what it reported (spec 131 FR-005).
  useMarkTaskReadOnOpen(taskId);
  const commands = useTaskCommands(workspaceId);
  /*
    The comments the current step has not run with (spec 235). Read here, not in
    the Comments panel alone, because only the drawer knows the step, its ways on
    and the commands that act on them; the query is the panel's own, so this is
    one read.
  */
  const comments = useTaskComments(workspaceId, taskId);
  const pendingVerb = usePendingTaskAction(taskId);
  const commentItems = comments.data?.items;
  const newIds = useMemo(
    () =>
      task && commentItems
        ? newCommentIds(
            commentItems,
            task.executions ?? [],
            location?.step?.stepId,
          )
        : undefined,
    [task, commentItems, location?.step?.stepId],
  );
  const commentsBlock =
    task && commentItems
      ? newCommentsBlock(task, location?.step, steps, commentItems)
      : null;
  const { startAll: startAllChildren } = useStartLinkedTasks(workspaceId);
  const { setTaskHidden } = useHideTask(workspaceId);
  const { copy } = useCopyToClipboard();
  const pendingCommand = useTaskCommandChannel((s) => s.pending);
  const takeCommand = useTaskCommandChannel((s) => s.take);
  // Taking the command clears it, which re-runs this effect; the timer that
  // runs it must outlive that re-run, so it is cleared only on unmount.
  const commandTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      window.clearTimeout(commandTimer.current);
    },
    [],
  );
  useEffect(() => {
    if (!task || pendingCommand?.taskId !== task.id) return;
    const command = takeCommand(task.id);
    /*
      Re-derived from the task as it stands now, not trusted from the request:
      a step or a way on the palette offered is run only if it is still on
      offer, so a task that moved meanwhile is never pushed somewhere stale.
      One that is not runs nothing, and says so: the reader chose an entry and
      should not be left wondering whether it was heard.
    */
    const runNextAction = (next: Exclude<TaskCommand, string>) => {
      const action = derivePrimaryAction(task, location?.step, steps);
      if (next.kind === 'start' && action.kind === 'start') {
        const target = [action.target, ...action.others].find(
          (candidate) => candidate.stepId === next.stepId,
        );
        if (target) commands.start(task, target);
        else toast(NO_LONGER_ON_OFFER);
        return;
      }
      if (next.kind === 'decide' && action.kind === 'decide') {
        const outcome = action.outcomes.find(
          (candidate) => candidate.to === next.to,
        );
        if (outcome) {
          const label =
            steps.find((candidate) => candidate.stepId === outcome.to)?.label ??
            outcome.to;
          commands.decide(task, outcome, label);
          return;
        }
      }
      toast(NO_LONGER_ON_OFFER);
    };
    // After the sheet's own opening focus has settled.
    commandTimer.current = window.setTimeout(() => {
      if (command !== null && typeof command === 'object') {
        runNextAction(command);
        return;
      }
      switch (command) {
        case 'stop':
          commands.stop(task);
          return;
        case 'run-again':
          commands.runAgain(task);
          return;
        case 'hide':
          setTaskHidden(task, task.hidden_at == null);
          return;
        case 'start-children':
          startAllChildren(task);
          return;
        case 'copy-link':
          void copy(window.location.href).then((ok) => {
            if (ok) toast('Link copied');
            else toast.error('The link could not be copied.');
          });
          return;
        default:
          return;
      }
    }, 0);
  }, [
    commands,
    copy,
    location?.step,
    pendingCommand,
    setTaskHidden,
    startAllChildren,
    steps,
    takeCommand,
    task,
  ]);

  /*
    One results read per drawer open (spec 095 FR-022). It answers two
    questions before any tab is drawn: is there a Results tab at all, and
    which tab does the drawer land on. The panel that shows the results
    subscribes to the same query; this is the observer that decides polling
    (FR-023), so there is exactly one interval, and only while Results is the
    tab shown and a run is in progress.
  */
  const live = task ? hasNonTerminalActivity([task]) : false;
  /*
    Read here rather than inside the panel so the tab can carry its count before
    anyone opens it (spec 100 FR-003). One request per drawer open; the panel's
    own use of the same hook is deduped by `taskRelationsKeys.direct`.
  */
  const relations = useDirectTaskRelations(workspaceId, taskId);
  const results = useTaskResults(workspaceId, taskId, {
    enabled: task !== undefined,
    poll: tab === 'results' && live,
    live,
  });
  // What the task created on the forge (spec 119 FR-014): read beside the
  // results, under the same rules, because it too decides whether the tab
  // exists, what it counts, and where the drawer lands.
  const artifactsQuery = useTaskArtifacts(workspaceId, taskId, {
    enabled: task !== undefined,
    poll: tab === 'results' && live,
    live,
  });
  const artifacts = artifactsReadOf(artifactsQuery);
  // A first read that failed leaves the drawer as it always was: three tabs,
  // Details. The read is tried again on the next open.
  const firstReadFailed = results.loadFailed && results.results === undefined;
  const offered = offeredTabs(
    firstReadFailed ? undefined : results.results,
    artifacts,
  );
  const resolved =
    !task || firstReadFailed
      ? (tab ?? 'details')
      : landingTab(tab, results.results, live, artifacts);

  // The address catches up with the tab the drawer chose, once per open, so a
  // copied link opens on what was being looked at and Back is not consumed.
  const wroteBack = useRef(false);
  useEffect(() => {
    if (tab !== undefined || resolved === 'pending' || wroteBack.current) {
      return;
    }
    wroteBack.current = true;
    onTabChange(resolved, { replace: true });
  }, [onTabChange, resolved, tab]);

  const showPending = usePendingDisplay(resolved === 'pending');
  // On a phone the drawer slides down before it leaves the address (spec 266):
  // closing there unmounted it at once, so it vanished rather than fell.
  const phone = usePhoneWidth();
  const [closing, setClosing] = useState(false);

  return (
    <Sheet
      open={!closing}
      /*
        Non-modal exactly while a preview is open (spec 140 FR-079).

        Base UI marks a modal dialog's sibling subtrees `inert` and
        `aria-hidden`, and `FilePreviewPanel` is rendered as a sibling on
        purpose (FR-031, FR-032) — so beside an open sheet the panel was on
        screen and simultaneously unreachable by keyboard and invisible to a
        screen reader, including its own close control. Dropping modality for
        the duration of the preview is what makes the companion region actually
        a companion; with no preview the sheet is modal exactly as before, so
        nothing about the ordinary task drawer changes.
      */
      modal={previewPath === undefined}
      /*
        The other half of dropping modality, and not optional. Base UI's
        non-modal dialog closes on an outside press *and* when focus moves
        outside it — so without this, the reader's first click or Tab into the
        preview dismissed the task sheet underneath and stripped `task` from
        the address. `disablePointerDismissal` is only set while the preview is
        open, so the ordinary drawer keeps dismissing on a backdrop press; with
        a preview up the way out is the sheet's own "Close task" (and Escape).
      */
      disablePointerDismissal={previewPath !== undefined}
      onOpenChange={(next) => {
        if (next) return;
        if (phone) setClosing(true);
        else onClose();
      }}
      onOpenChangeComplete={(next) => {
        if (!next && closing) onClose();
      }}
    >
      <SheetContent
        aria-label="Task detail"
        // The panel itself takes focus as it opens (spec 181), not its first control:
        // that was the title's rename pencil, ringed beside the title on every open.
        // A screen reader still hears "Task detail"; Tab reaches the pencil next.
        initialFocus={sheetContentRef}
        finalFocus={finalFocus}
        // The header carries its own close, positioned against the task title
        // rather than floating over it.
        showCloseButton={false}
        /*
          Two widths, not a drag handle (spec 096 FR-012 to FR-014). At 44 rem a
          line of `text-sm` holds about 75 characters at this padding — the top
          of the readable range, where 36 rem sat below its bottom. Wide mode is
          64 rem for long transcripts, and it is `lg:` on purpose: below the
          large breakpoint the drawer keeps its default cap or fills the
          viewport, whatever the setting says. Two fixed sizes keep every other
          measurement in the drawer predictable; a free resize would not, and
          can still be added behind `data-width` if two prove too few. The
          choice is a device preference in the store, like the sidebar's.
        */
        data-width={wide ? 'wide' : 'default'}
        className={cn('sm:max-w-[44rem]', wide && 'lg:max-w-[64rem]')}
        // A place, not a question: on a phone it rises to the top (spec 261).
        phoneLayout="full"
        ref={sheetContentRef}
      >
        {/* A div rather than a `header`: a sheet portals to `document.body`, so
            a `header` here has no sectioning ancestor and becomes a second
            `banner` beside the shell's own. `SheetHeader` and `DialogHeader`
            are both plain divs for the same reason. The heading inside names
            this region. */}
        {/* On a phone the controls take a row of their own, above where the
            task is and what it is called (spec 173): beside them the title had
            about 110px and wrapped a word to a line. */}
        <div className="border-border flex shrink-0 items-start justify-between gap-3 border-b p-4 max-sm:flex-col-reverse max-sm:items-stretch max-sm:gap-2 max-sm:pt-2">
          <div className="flex min-w-0 flex-col gap-1.5">
            {context ? (
              <div className="text-muted-foreground min-w-0 truncate text-xs">
                {context}
              </div>
            ) : null}
            {/*
              Where this task is, above what it is called. The drawer opened
              over a specific column of a specific board and the reader's eye
              came from there — naming the phase and step first is what connects
              the panel to the card it came out of.
            */}
            <p className="text-muted-foreground truncate text-xs">
              {location ? (
                phaseRepeatsStep(location) ? (
                  // "Done · Done" said one place twice; a phase named after its
                  // step is one name, and the board's own headers already agree.
                  location.stepLabel
                ) : (
                  <>
                    {location.phaseLabel}
                    <span aria-hidden="true"> · </span>
                    {location.stepLabel}
                  </>
                )
              ) : (
                // Neutral since the drawer is shown off the board too (spec
                // 111): what is true on both pages is that the task stands on
                // no step its workflow declares.
                'Not placed on a step'
              )}
            </p>

            {task ? (
              <TaskTitleField
                workspaceId={workspaceId}
                taskId={task.id}
                title={task.title}
              />
            ) : (
              <h2 className="text-foreground text-base leading-snug font-semibold">
                Task
              </h2>
            )}

            {/*
              The status, named in full rather than in the card's shorter
              wording, and here rather than in the details tab so it stays
              readable while someone is on Comments or Executions. `full`
              density also names the two states a card leaves silent — a task
              that has never run, and one that is finished — which a compact
              chip would render as nothing at all.
            */}
            {/*
              The identifier used to sit here as a fourth stacked line. It is
              the least-read thing in the header and it was costing height
              above a panel that scrolls, so it moved to the foot of the
              details tab, where a reference belongs.
            */}
            {state ? <ExecutionIndicator state={state} density="full" /> : null}
          </div>

          <div className="flex shrink-0 items-center gap-1 max-sm:justify-end">
            {/*
              A link to this task, without reading it out of the address bar.

              The address already identifies a task — the board carries `?task=`
              and `?tab=`, which is how a shared board link opens a drawer and
              how the workspace task list navigates. What was missing was any way
              to *get* that link. `location.href` is used rather than rebuilt so
              the copied address is exactly the one being looked at, tab and all.
            */}
            {/* What reaches you about this task (spec 131 US3). */}
            <FollowToggle workspaceId={workspaceId} taskId={taskId} />

            {/* Below `lg` the drawer has one width whatever this says (see
                the content's comment), so there it is not offered. */}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-pressed={wide}
              aria-label={wide ? 'Narrow drawer' : 'Widen drawer'}
              onClick={toggleTaskDrawerWide}
              className="max-lg:hidden"
            >
              {wide ? (
                <Minimize2Icon aria-hidden="true" strokeWidth={1.5} />
              ) : (
                <Maximize2Icon aria-hidden="true" strokeWidth={1.5} />
              )}
            </Button>

            {openOnBoard ? (
              /*
                The board around this task, for when the reader wants it — to
                move the task, or to see its neighbours. A link, so it can open
                in a new tab too, and it carries the tab being shown so the
                board opens on the same view.
              */
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                params={{ workspaceId, workflowKey: openOnBoard.workflowKey }}
                search={{
                  task: taskId,
                  // While the landing tab is still being worked out there is
                  // no tab to carry; the board picks its own, as here.
                  ...(resolved === 'pending' ? {} : { tab: resolved }),
                }}
                aria-label="Open on board"
                title="Open on board"
                className={buttonVariants({
                  variant: 'ghost',
                  size: 'icon-sm',
                })}
              >
                <KanbanSquareIcon aria-hidden="true" strokeWidth={1.5} />
              </Link>
            ) : null}

            <CopyButton
              text={() => window.location.href}
              label="Copy link to this task"
              copiedLabel="Link copied"
              failedLabel="Link not copied"
              idleIcon={<LinkIcon aria-hidden="true" strokeWidth={1.5} />}
            />

            {task ? (
              <TaskActionsMenu
                offerAiTool
                workspaceId={workspaceId}
                taskId={taskId}
                taskTitle={task.title}
                workflowId={task.workflow_id}
                isRunning={hasLiveExecution(task)}
                canRunAgain={canRunAgain(task.executions ?? [])}
                atGate={location?.step?.kind === 'human_gate'}
                task={task}
                onDeleted={onClose}
                waitingOnChildren={
                  state?.kind === 'live' && state.children !== undefined
                }
              />
            ) : null}

            <SheetClose
              render={<Button variant="ghost" size="icon-sm" />}
              aria-label="Close task"
            >
              <XIcon aria-hidden="true" strokeWidth={1.5} />
            </SheetClose>
          </div>
        </div>

        {/*
          `ui/tabs`, shared with the file preview's strip. These were filled
          pills and those were underlines, and the preview's own comment had
          already argued the pills read as text and said nothing about
          switching what is below. Neither strip moved on arrow keys, which
          `role="tablist"` promises they will.
        */}
        <TabsList
          aria-label="Task sections"
          // Tighter on a phone, where at the default spacing the fifth tab
          // fell past the edge (spec 173). The 16px gutter, as everywhere
          // else (spec 270); the strip scrolls if a count still pushes it.
          className="border-border shrink-0 border-b px-4 max-sm:gap-2.5"
        >
          {offered.map((key) => (
            <TabsTrigger
              key={key}
              id={`task-tab-${key}`}
              panelId={`task-panel-${key}`}
              selected={key === resolved}
              count={
                key === 'results'
                  ? resultsCount(results.results, artifacts)
                  : key === 'relations'
                    ? relationsCount(relations.data)
                    : tabCount(key, task)
              }
              onClick={() => {
                onTabChange(key);
              }}
            >
              {TASK_TAB_LABELS[key]}
            </TabsTrigger>
          ))}
        </TabsList>

        <SheetBody
          role="tabpanel"
          id={`task-panel-${resolved}`}
          aria-labelledby={
            resolved === 'pending' ? undefined : `task-tab-${resolved}`
          }
          aria-busy={resolved === 'pending' ? true : undefined}
          className={
            task
              ? 'flex flex-col gap-4 p-4 pb-10'
              : 'flex flex-col items-center justify-center gap-2 p-8 text-center'
          }
        >
          {task && resolved === 'pending' ? (
            showPending ? (
              <ResultsPlaceholder />
            ) : null
          ) : task ? (
            <>
              {resolved === 'details' && (
                <TaskDetailsPanel
                  workspaceId={workspaceId}
                  workflowKey={workflowKey}
                  task={task}
                  location={location}
                  stepLabels={stepLabels}
                  labelRegistry={labelRegistry}
                  onNarrowToLabel={onNarrowToLabel}
                  descriptionDraft={descriptionDraft}
                  onDescriptionDraftChange={setDescriptionDraft}
                />
              )}
              {resolved === 'executions' && (
                <TaskExecutionsPanel
                  task={task}
                  workspaceId={workspaceId}
                  stepLabels={stepLabels}
                  workflowKey={workflowKey}
                  previewPath={previewPath}
                  onOpenComments={() => {
                    onTabChange('comments');
                  }}
                />
              )}
              {resolved === 'comments' && (
                <TaskCommentsPanel
                  workspaceId={workspaceId}
                  taskId={taskId}
                  task={task}
                  stepLabels={stepLabels}
                  previewPath={previewPath}
                  newIds={newIds}
                  actionBlock={
                    task && commentsBlock ? (
                      <NewCommentsBlock
                        state={commentsBlock}
                        pendingLabel={
                          pendingVerb ? PENDING_LABEL[pendingVerb] : undefined
                        }
                        onRunAgain={() => {
                          commands.runAgain(task);
                        }}
                        onDecide={(outcome) => {
                          commands.decide(
                            task,
                            outcome,
                            steps.find((step) => step.stepId === outcome.to)
                              ?.label ?? outcome.to,
                          );
                        }}
                        onShowRun={() => {
                          onTabChange('executions');
                        }}
                      />
                    ) : undefined
                  }
                />
              )}
              {resolved === 'relations' && (
                <TaskRelationsPanel workspaceId={workspaceId} task={task} />
              )}
              {resolved === 'results' && (
                <TaskResultsPanel
                  workspaceId={workspaceId}
                  taskId={taskId}
                  task={task}
                  stepLabels={stepLabels}
                  previewPath={previewPath}
                />
              )}
            </>
          ) : (
            <>
              <p className="text-foreground text-sm font-medium">
                This task could not be read
              </p>
              <p className="text-muted-foreground max-w-prose text-sm">
                This workspace has no task with this identifier. It may have
                been removed or hidden, or the address may name a task from
                another workspace.
              </p>
            </>
          )}
        </SheetBody>
      </SheetContent>

      {/*
        The file preview, beside the sheet rather than inside it (FR-031). A
        sibling of `SheetContent` on purpose: nested in it, the panel would
        inherit the dialog's focus trap and its overflow, and Escape would
        have two things to mean (FR-032).

        The offset is written here, in the file that owns the width it offsets
        past (FR-074, F19): `SheetContent`'s `sm:max-w-[44rem]` / wide
        `lg:max-w-[64rem]` above is the other half of each pair below — change
        one and change the other (the drawer-level tests read both off the live
        elements). Both are *literals in this file* because Tailwind scans
        source text: a composed `` `sm:right-[${width}]` `` is a class that was
        never generated, and the panel would silently sit at offset zero,
        covering the sheet. The same `wide` boolean decides both, so the two
        cannot disagree; a `ResizeObserver` on a portalled dialog would resolve
        a frame late and the panel would jump on open and on every toggle.
      */}
      {previewPath !== undefined ? (
        <FilePreviewPanel
          workspaceId={workspaceId}
          path={previewPath}
          offsetClassName={
            wide
              ? 'sm:right-[44rem] sm:w-[calc(100vw-44rem)] lg:right-[64rem] lg:w-[calc(100vw-64rem)]'
              : 'sm:right-[44rem] sm:w-[calc(100vw-44rem)]'
          }
          fallbackFocusRef={sheetContentRef}
          onClose={onPreviewClose}
        />
      ) : null}
    </Sheet>
  );
}
